import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import values from 'lodash/values';
import moment from 'moment';
import ClipboardJS from 'clipboard';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

import { Typography, withTheme } from '@material-ui/core';
import { unixTimestampToDate } from 'Internals/datetime';

import { JwtActions } from 'Redux/thunks/jwt.thunks';
import Button from 'Common/Button/Button';
import { toastAdded } from 'Redux/thunks/global.thunks';
import SesamModal from 'Common/SesamModal/SesamModal';
import { DangerText } from 'Common/Text/DangerText';
import ExpansionPanel from '../../../components/expansion-panel';
import AddJwtForm from '../../../components/add-jwt';
import PromiseButton from '../../../components/promise-button';
import KeyValue from '../../../components/key-value';
import CodeViewer from '../../../components/code-viewer';
import { SubActions } from 'Redux/thunks/subscriptions.thunks';

import './style.css';
import { TestID } from '../../../testID';
import { JwtItem } from './JwtItem.view';
import { updateJWT } from 'Utils/jwt.utils';

const isExpired = (expiryDate) => {
  const now = new Date();
  if (expiryDate < now) return 'expired';
  const future = now.setDate(now.getDate() + 14);
  if (expiryDate < future) return 'expiring';
  return false;
};

const style = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

function NewJwtModal({ isOpen, jwtString, clientSecret, onClose }) {
  return (
    <SesamModal isOpen={isOpen} onClose={onClose} style={style}>
      <div className="jwt-modal">
        <h2 className="heading-component" data-testid={TestID.NewJwtModalHeader}>
          New JWT created
        </h2>
        <p style={{ marginTop: 0 }} data-testid={TestID.NewJwtModalDescription}>
          This is the string of the newly created token:
        </p>
        <h2 className="heading-component" data-testid={TestID.NewJwtModalHeader}>
          New JWT created
        </h2>
        <p style={{ marginTop: 0 }} data-testid={TestID.NewJwtModalDescription}>
          This is the string of the newly created token:
        </p>
        <CodeViewer code={jwtString} forceWrap style={{ maxWidth: '20rem' }} />
        <div className="jwt-modal__copy-box">
          <DangerText className="jwt-modal__copy-text">
            This is the only time this string is provided. <b>Make sure you save it.</b>
          </DangerText>
          <Button
            text="Copy to clipboard"
            data-clipboard-text={jwtString}
            className="jwt-modal__copy-button"
          />
        </div>
        {clientSecret && (
          <React.Fragment>
            <p style={{ marginTop: 0 }}>This is the client secret:</p>
            <CodeViewer code={clientSecret} forceWrap style={{ maxWidth: '20rem' }} />
            <div className="jwt-modal__copy-box">
              <DangerText className="jwt-modal__copy-text">
                This is the only time this secret is provided. <b>Make sure you save it.</b>
              </DangerText>
              <Button
                text="Copy to clipboard"
                data-clipboard-text={clientSecret}
                className="jwt-modal__copy-button"
              />
            </div>
          </React.Fragment>
        )}
        <hr style={{ width: '100%' }} />
        <Button
          text="Dismiss"
          onClick={onClose}
          style={{ alignSelf: 'flex-end', marginTop: '1rem' }}
          data-testid={TestID.NewJwtModalDismissButton}
        />
      </div>
    </SesamModal>
  );
}

NewJwtModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  jwtString: PropTypes.string.isRequired,
  clientSecret: PropTypes.string,
  onClose: PropTypes.func,
};

NewJwtModal.defaultProps = {
  jwt_string: "JWT string doesn't exist",
  clientSecret: '',
  onClose: () => {
    return;
  },
};

class SettingsSubscriptionJWT extends React.Component {
  constructor(props) {
    super(props);

    this.handleAdd = (jwt) => {
      jwt.owner = updateJWT(this.props.members, jwt.owner);

      return this.props.addJwt(this.props.subId, jwt).then(({ jwtString, clientSecret }) => {
        return this.openModal(jwtString, clientSecret);
      });
    };

    this.uuidToEmail = (jwt) => {
      const member = this.props.members.find((member) => member.user.user_id === jwt.owner);

      jwt.owner = member?.user?.email || '';
      return jwt;
    };

    this.openModal = (jwtString, clientSecret) => {
      this.setState({
        modalOpened: true,
        tempJwtString: jwtString,
        tempClientSecret: clientSecret,
      });
    };

    this.closeModal = () => {
      if (confirm('Are you sure you want to continue?')) {
        this.setState({ modalOpened: false });
      }
    };

    this.handleDelete = (jwt) => {
      if (confirm(`Are you sure you want to delete the "${jwt.name}" token?`)) {
        return this.props.deleteJwt(this.props.subId, jwt.id);
      }
      return Promise.reject();
    };

    this.handleEdit = (jwt) => {
      try {
        console.log('inside the handleEdit', jwt);
        return this.props.editJwt(this.props.subId, jwt);
      } catch (err) {
        console.error(err);
        return Promise.reject();
      }
    };

    this.props.loadAllJwts(this.props.subId);
    this.props.loadMembers(this.props.subId);

    this.clipboard = new ClipboardJS('.jwt-modal__copy-button');
    this.clipboard.on('success', () => {
      this.props.addToast('Copied to clipboard');
    });

    this.sortJWTs = () => {
      let jwts = cloneDeep(this.props.allJwts);

      return jwts.sort((a, b) => {
        let x = a?.name?.toLowerCase();
        let y = b?.name?.toLowerCase();

        if (x > y) return 1;
        else if (x < y) return -1;
        return 0;
      });
    };

    this.state = {
      tempJwtString: '',
      tempClientSecret: '',
      modalOpened: false,
    };
  }

  componentWillUnmount() {
    this.clipboard.destroy();
  }

  render() {
    return (
      <main className="scrollArea">
        <h1 className="heading-page" data-testid={TestID.JsonWebTokenTabHeader}>
          JSON Web Tokens
        </h1>
        <div className="row" id="jwt-row">
          <div className="col gr-secondary">
            <AddJwtForm handleAdd={this.handleAdd} />
          </div>
          <div className="col gr-primary">
            <h2
              className="heading-section"
              data-testid={TestID.JsonWebTokenTabExistingTokensHeader}
            >
              Existing JWTs
            </h2>
            {this.props.allJwts.length > 0 && this.props.members.length > 0 && (
              <ul className="expansion-panel-list">
                {this.sortJWTs().map((jwt) => (
                  <li key={jwt.id}>
                    <JwtItem
                      jwt={this.uuidToEmail(jwt)}
                      onDelete={this.handleDelete}
                      onEdit={this.handleEdit}
                      theme={this.props.theme}
                      members={this.props.members}
                    />
                  </li>
                ))}
              </ul>
            )}
            {this.props.allJwts.length === 0 && (
              <p>There are no JWTs associated with this subscription.</p>
            )}
          </div>
        </div>
        <NewJwtModal
          isOpen={this.state.modalOpened}
          onClose={this.closeModal}
          jwtString={this.state.tempJwtString}
          clientSecret={this.state.tempClientSecret}
        />
      </main>
    );
  }
}

const jwtProps = {
  description: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  payload: PropTypes.shape({
    exp: PropTypes.number.isRequired,
  }),
  audit: PropTypes.shape({
    created_by: PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    }),
    last_modified_by: PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
};

SettingsSubscriptionJWT.propTypes = {
  addJwt: PropTypes.func.isRequired,
  addToast: PropTypes.func.isRequired,
  allJwts: PropTypes.arrayOf(PropTypes.shape(jwtProps)),
  deleteJwt: PropTypes.func.isRequired,
  loadAllJwts: PropTypes.func.isRequired,
  loadMembers: PropTypes.func.isRequired,
  subId: PropTypes.string.isRequired,
  theme: PropTypes.object,
  members: PropTypes.array,
};

function mapStateToProps(state) {
  return {
    allJwts: state.jwt,
    subId: state.subscription.id,
    members: state.subscription.members,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    addJwt: (subId, jwt) => dispatch(JwtActions.add(subId, jwt)),
    deleteJwt: (subId, jwtId) => dispatch(JwtActions.delete(subId, jwtId)),
    editJwt: (subId, jwt) => dispatch(JwtActions.edit(subId, jwt)),
    loadAllJwts: (subId) => dispatch(JwtActions.loadAll(subId)),
    loadMembers: (subId) => dispatch(SubActions.loadMembers(subId)),
    addToast: (message) => dispatch(toastAdded({ message, type: 'success' })),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(SettingsSubscriptionJWT));
